import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { queryParamToHttp } from "../../../../shared/store/utils/store-utils.service";
import { firstValueFrom, Observable } from "rxjs";
import { EntityApiService } from "../../../../shared/store/entity-store/entity-api.service";
import { Tiers } from "../../models/tiers.model";
import { environment } from "../../../../../environments/environment";
import { CertificatBio } from "../../models/certificat-bio.model";
import { StatsTiers } from "../../models/stats-tiers.model";

@Injectable({ providedIn: "root" })
export class TiersApiService implements EntityApiService<Tiers> {
  loadOne(id: string): Observable<Tiers> {
    return this.httpClient.get<Tiers>(`${environment.api()}/tiers/${id}`);
  }

  httpClient = inject(HttpClient);

  create(entity: Tiers): Observable<Tiers> {
    return this.httpClient.post<Tiers>(`${environment.api()}/tiers`, entity);
  }

  remove(id: string): Observable<string> {
    throw new Error("Method not implemented.");
  }

  update(entity: Tiers): Observable<Tiers> {
    return this.httpClient.post<Tiers>(`${environment.api()}/tiers`, entity);
  }

  loadAll(): Observable<Tiers[]> {
    return this.httpClient.get<Tiers[]>(`${environment.api()}/tiers`);
  }

  loadAllFilter(filters: {
    search?: string;
    page?: number;
    isClient?: boolean;
    isFournisseur?: boolean;
    isTransporteur?: boolean;
    aucunRole?: boolean;
  }): Observable<Tiers[]> {
    const query: Record<string, string | number | boolean> = {};
    if (filters.search) query["search"] = filters.search;
    if (filters.page != null) query["page"] = filters.page;
    if (filters.isClient) query["isClient"] = true;
    if (filters.isFournisseur) query["isFournisseur"] = true;
    if (filters.isTransporteur) query["isTransporteur"] = true;
    if (filters.aucunRole) query["aucunRole"] = true;
    return this.httpClient.get<Tiers[]>(
      `${environment.api()}/tiers/navfilter`,
      { params: queryParamToHttp(query) },
    );
  }

  loadFournisseursForArticle(id: string) {
    return this.httpClient.get<Tiers[]>(
      `${environment.api()}/tiers/article/${id}`,
    );
  }
  loadSuggestionForArticleId(articleId: string) {
    return this.httpClient.get<Tiers>(
      `${environment.api()}/tiers/article/${articleId}/suggestion`,
    );
  }

  loadAllTransporteurs() {
    return this.httpClient.get<Tiers[]>(
      `${environment.api()}/tiers/transporteur`,
    );
  }

  saveCertificat(certificat: CertificatBio): Observable<Tiers> {
    return this.httpClient.post<Tiers>(
      `${environment.api()}/tiers/certificat`,
      certificat,
    );
  }

  loadCertificat(tiersId: string): Observable<CertificatBio> {
    return this.httpClient.get<CertificatBio>(
      `${environment.api()}/tiers/certificat/tiersId/${tiersId}`,
    );
  }

  deleteCertificat(tiersId: string): Observable<CertificatBio> {
    return this.httpClient.delete<CertificatBio>(
      `${environment.api()}/tiers/certificat/tiersId/${tiersId}`,
    );
  }

  stats(payload: { tiersId: string; dateDebut: Date; dateFin: Date }) {
    return this.httpClient.post<StatsTiers>(
      `${environment.api()}/stats/tiers`,
      payload,
    );
  }

  loadNextReferencePreview(): Observable<{ code: string }> {
    return this.httpClient.get<{ code: string }>(
      `${environment.api()}/tiers/next-reference-preview`,
    );
  }

  loadAnalyticsSummary(tiersId: string): Observable<TiersAnalyticsSummary> {
    return this.httpClient.get<TiersAnalyticsSummary>(
      `${environment.api()}/tiers/${tiersId}/analytics-summary`,
    );
  }

  /**
   * Aperçu de la prochaine référence tiers (back read-only, n'incrémente pas
   * le compteur). Utilisé par le form pour pré-remplir le champ "Référence"
   * à l'ouverture en mode création.
   */
  async getNextReferencePreview(): Promise<string> {
    const res = await firstValueFrom(
      this.httpClient.get<{ code: string }>(
        `${environment.api()}/tiers/next-reference-preview`,
      ),
    );
    return res.code;
  }
}

export interface TiersAnalyticsSummary {
  hasSales: boolean;
  hasPurchases: boolean;
  hasTransport: boolean;
}
